import { useState } from 'react';
import { Button } from '@helios/ui';
import { PERMISSIONS } from '@helios/authz';
import { ExecutivePriorityPanel } from '@/features/command-center/ExecutivePriorityPanel';
import { executivePriorityGrants } from '@/services/executivePrioritiesClient';
import { sessionScopeKey, useSessionScopeFence } from '@/hooks/useSessionScope';
import type { InvoiceDirection } from './financeInvoices.types';

/** Internal accountable human work only; never an invoice, payment or outreach action. */
export function FinanceInvoiceFollowups({
  direction,
  id,
  compact = false,
}: {
  direction: InvoiceDirection;
  id?: string;
  compact?: boolean;
}) {
  const taskAccess = useSessionScopeFence(executivePriorityGrants.read);
  const invoiceAccess = useSessionScopeFence(
    direction === 'ar'
      ? PERMISSIONS.FINANCE_AR_INVOICES_READ
      : PERMISSIONS.FINANCE_AP_INVOICES_READ,
  );
  if (!taskAccess.enabled || !invoiceAccess.enabled)
    return (
      <p className="text-muted-foreground text-xs">
        Invoice follow-ups require current internal task and corresponding invoice read access. No
        owner assignment or completed action is inferred.
      </p>
    );
  return (
    <div
      key={JSON.stringify([sessionScopeKey(taskAccess.identity), direction, id])}
      className={compact ? 'flex h-full min-h-0 min-w-0 flex-col gap-2' : 'min-w-0 space-y-2'}
    >
      <p className="text-muted-foreground text-xs">
        {compact
          ? 'Internal human tasks only. Completion does not confirm collection, payment or bank settlement.'
          : 'Recorded internal follow-ups only. Completion verifies this human task and its pinned document evidence; it does not confirm collection, payment, dispute resolution, delivery or bank settlement.'}
      </p>
      <ExecutivePriorityPanel
        invoiceScope={{
          sourceType: direction === 'ar' ? 'ArInvoice' : 'ApInvoice',
          ...(id ? { sourceId: id } : {}),
        }}
        heading={`${direction === 'ar' ? 'Receivable' : 'Payable'} follow-ups`}
        compact={compact}
      />
    </div>
  );
}
export function FinanceInvoiceFollowupRail() {
  const [direction, setDirection] = useState<InvoiceDirection>('ar');
  return (
    <section className="space-y-3" aria-label="Internal finance follow-ups">
      <div className="flex flex-wrap gap-2" aria-label="Follow-up invoice direction">
        <Button
          size="sm"
          variant={direction === 'ar' ? 'secondary' : 'outline'}
          aria-pressed={direction === 'ar'}
          onClick={() => setDirection('ar')}
        >
          Receivable follow-ups
        </Button>
        <Button
          size="sm"
          variant={direction === 'ap' ? 'secondary' : 'outline'}
          aria-pressed={direction === 'ap'}
          onClick={() => setDirection('ap')}
        >
          Payable follow-ups
        </Button>
      </div>
      <FinanceInvoiceFollowups direction={direction} />
    </section>
  );
}
